import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from "@ant-design/icons";
import { useNavigate } from "@tanstack/react-router";
import { Button, Modal, Result, Steps } from "antd";
import queryClient from "api/queryClient.ts";
import { useCreationStatusQuery, useStoreTenantMutation } from "api/Tenant/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser.ts";
import { FC, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FormId } from "types/models";

import { createTenantSchema, CreateTenantFormData } from "./formSchema";
import { TenantInfoStep } from "./steps/TenantInfoStep";

interface CreateTenantModalProps {
    open: boolean;
    onClose: () => void;
}

const STAGE_KEYS = ["setting_up_workspace", "creating_database"] as const;

const CREATION_TIMEOUT = 300_000; // 5 minutes

const defaultValues: CreateTenantFormData = {
    title: "",
    currency_code: null as unknown as string,
    accept_terms: false as unknown as true,
};

export const CreateTenantModal: FC<CreateTenantModalProps> = ({ open, onClose }) => {
    const { t } = usePaldockTranslation("modals.create-tenant");
    const { t: tC } = usePaldockTranslation("common");
    const navigate = useNavigate();
    const userUtils = useUser();

    const [creatingTenantId, setCreatingTenantId] = useState<string | null>(null);
    const [currentStage, setCurrentStage] = useState(0);
    const [creationDone, setCreationDone] = useState(false);
    const [creationError, setCreationError] = useState(false);
    const [createdSlug, setCreatedSlug] = useState<string | null>(null);

    const stageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dbStageDelay = useRef(0);

    const storeTenantMutation = useStoreTenantMutation();
    const creationStatusQuery = useCreationStatusQuery(creatingTenantId);

    // Stage progression timers
    useEffect(() => {
        if (!creatingTenantId) return;

        // Advance to "Creating database" after random 8-11s
        stageTimer.current = setTimeout(() => {
            setCurrentStage((prev) => Math.max(prev, 1));
        }, dbStageDelay.current);

        // Timeout timer
        timeoutTimer.current = setTimeout(() => {
            setCreationError(true);
            setCreatingTenantId(null);
        }, CREATION_TIMEOUT);

        return () => {
            if (stageTimer.current) clearTimeout(stageTimer.current);
            if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
            stageTimer.current = null;
            timeoutTimer.current = null;
        };
    }, [creatingTenantId]);

    // Handle creation complete
    useEffect(() => {
        if (!creationStatusQuery.data?.created) return;

        const slugName = creationStatusQuery.data.slug_name;

        // Move all stages to finished
        setCurrentStage(STAGE_KEYS.length);

        // Brief delay before showing success screen
        const timer = setTimeout(() => {
            setCreationDone(true);
            setCreatedSlug(slugName);
            setCreatingTenantId(null);
            queryClient.invalidateQueries({ queryKey: ["me"] });
        }, 500);

        return () => clearTimeout(timer);
    }, [creationStatusQuery.data?.created, creationStatusQuery.data?.slug_name]);

    // Handle polling error
    useEffect(() => {
        if (creationStatusQuery.isError) {
            setCreationError(true);
            setCreatingTenantId(null);
        }
    }, [creationStatusQuery.isError]);

    const handleSubmit = async (data: CreateTenantFormData) => {
        try {
            const response = await storeTenantMutation.mutateAsync({
                title: data.title,
                slug_name: null,
                async: true,
                show_in_public_catalog: false,
                currency_code: data.currency_code,
            });

            if ("created" in response) {
                dbStageDelay.current = Math.floor(Math.random() * 3000) + 8000;
                setCreatingTenantId(response.id);
            } else {
                toast.error(t("messages.error"));
            }
        } catch {
            toast.error(t("messages.error"));
        }
    };

    const handleClose = () => {
        if (isCreating) return;
        resetState();
        onClose();
    };

    const resetState = () => {
        setCreatingTenantId(null);
        setCurrentStage(0);
        setCreationDone(false);
        setCreationError(false);
        setCreatedSlug(null);
    };

    const handleRetry = () => {
        resetState();
    };

    const handleGoToWorkspace = () => {
        if (!createdSlug) return;
        userUtils
            .fetchUser()
            .then(() => {
                onClose();
                resetState();
                navigate({
                    to: "/$tenantSlug/setting/general",
                    params: { tenantSlug: createdSlug },
                });
            })
            .catch(() => {
                toast.error(t("messages.error"));
            });
    };

    const isCreating = !!creatingTenantId || storeTenantMutation.isPending;
    const showForm = !isCreating && !creationDone && !creationError;

    const getStepStatus = (stepIndex: number): "finish" | "process" | "wait" => {
        if (creationDone) return "finish";
        if (currentStage > stepIndex) return "finish";
        if (currentStage === stepIndex) return "process";
        return "wait";
    };

    const getStepIcon = (stepIndex: number) => {
        const status = getStepStatus(stepIndex);
        if (status === "process") return <LoadingOutlined />;
        return undefined;
    };

    const renderContent = () => {
        // State B: Creating — stage progression
        if (isCreating) {
            return (
                <div className="py-8 px-4">
                    <Steps
                        direction="vertical"
                        size="small"
                        current={currentStage}
                        items={STAGE_KEYS.map((key, index) => ({
                            title: t(`stages.${key}`),
                            status: getStepStatus(index),
                            icon: getStepIcon(index),
                        }))}
                    />
                </div>
            );
        }

        // State C: Success
        if (creationDone) {
            return (
                <Result
                    icon={<CheckCircleFilled className="text-green-500" />}
                    title={t("messages.success_title")}
                    subTitle={t("messages.success_description")}
                    extra={
                        <Button type="primary" onClick={handleGoToWorkspace}>
                            {t("messages.go_to_workspace")}
                        </Button>
                    }
                />
            );
        }

        // State D: Error
        if (creationError) {
            return (
                <Result
                    icon={<CloseCircleFilled className="text-red-500" />}
                    status="error"
                    title={t("messages.timeout")}
                    extra={
                        <Button type="primary" onClick={handleRetry}>
                            {tC("try_again")}
                        </Button>
                    }
                />
            );
        }

        // State A: Form
        return (
            <GeneralFormProvider<CreateTenantFormData>
                id={FormId.CreateTenantForm}
                schema={createTenantSchema}
                defaultValues={defaultValues}
                onSubmit={handleSubmit}
                resetOnSubmit={false}
            >
                <TenantInfoStep />
            </GeneralFormProvider>
        );
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={isCreating ? t("stages.title") : t("title")}
            okText={tC("create")}
            cancelText={tC("cancel")}
            okButtonProps={{
                htmlType: "submit",
                form: FormId.CreateTenantForm,
                loading: storeTenantMutation.isPending,
            }}
            cancelButtonProps={{ disabled: isCreating }}
            closable={!isCreating}
            maskClosable={!isCreating}
            keyboard={!isCreating}
            footer={showForm ? undefined : null}
            destroyOnClose
        >
            {renderContent()}
        </Modal>
    );
};
